/**
 * useUsersFeedback — feedback-list state + cursor pagination.
 *
 * Ports the list-state half of Flutter's `UsersFeedbackScreenState`
 * (lib/presentation/screens/feedback/users_feedback_screen.dart:28-99):
 *   - state: `_items`, `_hasMore = true`, `_lastFetchedItem`, `_isFetching`
 *     (users_feedback_screen.dart:28-32).
 *   - `_getData()` (users_feedback_screen.dart:74-99): early-return when
 *     `!_hasMore || _isFetching`; set fetching; fetch a page; dedup-append via
 *     `addAllUnique`; advance `_lastFetchedItem` / `_hasMore`; clear fetching.
 *   - scroll-to-bottom trigger (users_feedback_screen.dart:57-64) — on web the
 *     `FlexibleHeaderList.onLoadMore` edge callback (driven by the page, Task 11)
 *     replaces the `ScrollController` listener and calls `loadMore()`.
 *
 * Dedup parity: Flutter `addAllUnique` (lib/domain/extensions/list_extensions.dart:5-11)
 * uses `contains`, which for a model without `==` is reference identity. On the
 * web a cursor overlap could re-deliver an already-seen feedback document, so we
 * dedup defensively by `id` to avoid duplicate React keys — see the Task 7 plan
 * "Web work" note.
 *
 * No `useBasePage` here — that is the page's job (presentation.md → Page
 * lifecycle). The page (Task 11) calls `init()` from `onInit` and `loadMore()`
 * from the list's load-more edge callback.
 *
 * Web parity reference for the cursor-paginated hook shape (opaque `PageCursor`
 * forwarded verbatim, ref-style in-flight guard mirroring Flutter's `_isFetching`):
 * useInboxNotifications.ts.
 */
import { useCallback, useRef, useState } from 'react';
import type { PageCursor } from '@/data/types/json';
import type { InAppFeedbackEntity } from '@/domain/entities/feedback/inAppFeedbackEntity';
import { FetchUsersFeedbackUseCase } from '@/domain/usecases/feedback/fetchUsersFeedbackUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useLocale } from '@/presentation/contexts/LocaleContext';

export interface UseUsersFeedbackResult {
  items: InAppFeedbackEntity[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  init: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export function useUsersFeedback(
  fetchUsersFeedbackUseCase: FetchUsersFeedbackUseCase = new FetchUsersFeedbackUseCase(),
): UseUsersFeedbackResult {
  const { l10n } = useLocale();

  const [items, setItems] = useState<InAppFeedbackEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs mirror the live state for the guards inside the stable callbacks, which
  // would otherwise close over stale values (useInboxNotifications pattern).
  // `cursorRef` is the opaque `_lastFetchedItem` cursor forwarded verbatim to the
  // use case (users_feedback_screen.dart:30/86-92).
  const cursorRef = useRef<PageCursor | undefined>(undefined);
  const hasMoreRef = useRef(true);
  // The in-flight guard — Flutter's `_isFetching` (users_feedback_screen.dart:31).
  // A ref so rapid scroll-driven `loadMore()` calls never double-fetch.
  const isFetchingRef = useRef(false);
  // Guards against `init()` running twice (e.g. a double `onInit`); Flutter fires
  // the first `_getData()` once from the post-frame callback
  // (users_feedback_screen.dart:53-55).
  const hasInitRef = useRef(false);

  const init = useCallback(async () => {
    if (hasInitRef.current) return;
    hasInitRef.current = true;

    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // First page: no cursor (users_feedback_screen.dart:30/86-90).
      const result = await fetchUsersFeedbackUseCase.execute(undefined);
      setItems(result.items);
      cursorRef.current = result.cursor;
      hasMoreRef.current = result.hasMore;
      setHasMore(result.hasMore);
    } catch (e) {
      appLogger.error('Error fetching users feedback', 'useUsersFeedback.init', {
        error: e as Error,
      });
      setError(l10n.common.genericError);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [fetchUsersFeedbackUseCase, l10n]);

  const loadMore = useCallback(async () => {
    // Mirrors `_getData`'s `!_hasMore || _isFetching` guard
    // (users_feedback_screen.dart:75-77).
    if (isFetchingRef.current || !hasMoreRef.current) return;

    isFetchingRef.current = true;
    setIsLoadingMore(true);

    try {
      // Next page: forward the stored opaque cursor verbatim
      // (users_feedback_screen.dart:86-90).
      const result = await fetchUsersFeedbackUseCase.execute(cursorRef.current);

      // dedup-append by `id` (addAllUnique parity; list_extensions.dart:5-11):
      // fold appended ids into `seen` as we build so an intra-page duplicate is
      // also dropped, matching Flutter's `contains`-against-the-growing-list.
      setItems(prev => {
        const seen = new Set(prev.map(item => item.id));
        const appended: InAppFeedbackEntity[] = [];
        for (const item of result.items) {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            appended.push(item);
          }
        }
        return appended.length > 0 ? [...prev, ...appended] : prev;
      });

      cursorRef.current = result.cursor;
      hasMoreRef.current = result.hasMore;
      setHasMore(result.hasMore);
    } catch (e) {
      appLogger.error(
        'Error fetching more users feedback',
        'useUsersFeedback.loadMore',
        { error: e as Error },
      );
      setError(l10n.common.genericError);
    } finally {
      isFetchingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [fetchUsersFeedbackUseCase, l10n]);

  return {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    init,
    loadMore,
  };
}
